import { useEffect, useRef, useState } from 'react';

import authService from 'modules/auth/domain/services/auth-service';
import { authCheckSilent } from 'modules/auth/store/actions';
import { useAppDispatch } from 'store/hooks';

import { useAuth } from './use-auth';

/**
 * Один раз проверяет сохранённый вход без переходов (`authCheckSilent`) — для экранов, где `SecureRoute` не
 * монтируется: заглушка телефона и планшета. Пользователь попадает в стор, и Amplitude узнаёт его через
 * `AnalyticsIdentityTracker` (`src/App.tsx`).
 *
 * Возвращает `true`, когда ждать больше нечего: пользователь уже есть, токена нет или проверка закончилась.
 * По этому признаку событие заглушки уходит уже с `user_id`.
 */
export function useSilentAuthCheck(enabled: boolean): boolean {
  const dispatch = useAppDispatch();
  const { user, isChecking } = useAuth();
  const started = useRef(false);
  const [isDone, setDone] = useState(false);

  const hasToken = authService.hasToken();

  useEffect(() => {
    // `isChecking` — уже идёт обычная проверка: дождаться её, а не слать вторую.
    if (!enabled || started.current || user || isChecking || !hasToken) {
      return;
    }

    started.current = true;
    dispatch(authCheckSilent()).finally(() => setDone(true));
  }, [enabled, user, isChecking, hasToken, dispatch]);

  return Boolean(user) || !hasToken || isDone;
}
